<script setup lang="ts">
import { ref, watch, onUnmounted } from "vue";
import { Info } from "@lucide/vue";
import { Button } from "@/components/ui/button";

const isExpanded = ref(false);
let autoCollapseTimer: ReturnType<typeof setTimeout> | null = null;

/** Expand or collapse the weather legend. */
function toggle() {
  isExpanded.value = !isExpanded.value;
}

/** Restart the timeout that closes the expanded legend. */
function resetAutoCollapse() {
  if (autoCollapseTimer) clearTimeout(autoCollapseTimer);
  if (isExpanded.value) {
    autoCollapseTimer = setTimeout(() => {
      isExpanded.value = false;
    }, 8000);
  }
}

watch(isExpanded, (val) => {
  if (val) {
    resetAutoCollapse();
  } else if (autoCollapseTimer) {
    clearTimeout(autoCollapseTimer);
    autoCollapseTimer = null;
  }
});

onUnmounted(() => {
  if (autoCollapseTimer) clearTimeout(autoCollapseTimer);
});
</script>

<template>
  <div class="flex items-start">
    <Button
      size="icon-small"
      radius="control"
      class="shrink-0"
      @click="toggle"
      title="Toggle legend"
      aria-label="Toggle legend"
      :aria-expanded="isExpanded"
    >
      <Info class="h-4 w-4" />
    </Button>
    <div
      class="legend-content overflow-hidden"
      :class="
        isExpanded ? 'expanded max-w-[600px] opacity-100' : 'max-w-0 opacity-0'
      "
    >
      <div class="flex flex-col gap-1.5 pl-2 whitespace-nowrap">
        <slot />
      </div>
    </div>
  </div>
</template>

<!-- Width and opacity deliberately use different reveal/close timings. -->
<style scoped>
.legend-content {
  transition:
    max-width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.15s ease;
}
.legend-content.expanded {
  transition:
    max-width 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s ease 0.1s;
}
</style>
